import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  useListCashRegisterSessionsQuery,
  useListMarktArticlesQuery,
  useListMarktCustomersQuery,
  useListMarktStocksQuery,
  useProcessTxMutation,
  useLazyGetMarktReceiptQuery,
} from '../../../redux/api/marktApi';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import SelectCashRegisterModal from './SelectCashRegisterModal';
import CreateCustomerModal from './CreateCustomerModal';
import PaymentModal from './PaymentModal';
import PickingReceipt from './PickingReceipt';
import FiscalReceipt from './FiscalReceipt';
import { toast } from 'react-toastify';
import Decimal from 'decimal.js';
import { useApiErrorToast } from '../../../hooks/useApiError';

const MarktPOSInterface = () => {
  // State management
  const [basket, setBasket] = useState([]); // This will store items with 'amount'
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [responseDebug, setResponseDebug] = useState(''); // Not used in this version, but kept for consistency
  const [showPaymentModal, setShowPaymentModal] = useState(false); // NEW: State to control PaymentModal visibility

  // Reservation pickup: when arriving from the reservations page, the basket is
  // prefilled and the resulting sale is linked back to the reservation.
  const [reservationId, setReservationId] = useState(null);
  const [reservationCustomerName, setReservationCustomerName] = useState('');
  const [reservationCustomerEmail, setReservationCustomerEmail] = useState('');

  // Picking receipt printing (authoritative default from the cash register).
  const [printEnabled, setPrintEnabled] = useState(true);
  const [lastSale, setLastSale] = useState(null);
  const [printSignal, setPrintSignal] = useState(0);
  // Admin-only printer self-test (dummy picking receipt, no real sale).
  const [dummySale, setDummySale] = useState(null);

  // Customer fiscal receipt (Kundenbeleg) — off by default (on demand).
  const [printCustomerReceipt, setPrintCustomerReceipt] = useState(false);
  const [customerReceipt, setCustomerReceipt] = useState(null);
  const [lastTxId, setLastTxId] = useState(null);
  // Which hidden receipt the next window.print() should output.
  const [printTarget, setPrintTarget] = useState('picking');
  // Targets still to print this round; advanced by the 'afterprint' event so
  // both receipts can print one after the other when both are enabled.
  const printQueueRef = useRef([]);

  const [fetchReceipt] = useLazyGetMarktReceiptQuery();

  const location = useLocation();
  const navigate = useNavigate();
  const prefillDone = useRef(false);

  const { data: runningSessions, isLoading: sessionsLoading } =
    useListCashRegisterSessionsQuery();
  const { data: marktCustomers, isLoading: customersLoading } =
    useListMarktCustomersQuery();
  const { data: articles, isLoading: articlesLoading } =
    useListMarktArticlesQuery();
  const { data: stocks, isLoading: stocksLoading } = useListMarktStocksQuery();

  const [doProcessTx, { isLoading: processTxIsLoading }] =
    useProcessTxMutation(); // Renamed isLoading to processTxIsLoading for clarity

  const authState = useSelector((state) => state.auth);
  const apiErrorToast = useApiErrorToast();

  // --- Derived State (Memoized for performance) ---

  // Create a map for quick stock lookup (articleId -> sellable quantity).
  // Sellable = physical available − stock held by confirmed reservations, so
  // the POS can't sell into reserved stock.
  const stockMap = useMemo(() => {
    if (stocksLoading || !stocks) {
      return new Map();
    }
    return new Map(
      stocks
        .filter((s) => s.article != null && s.quantity_sellable != null)
        .map((stock) => [stock.article, stock.quantity_sellable])
    );
  }, [stocks, stocksLoading]);

  // Keep the print toggle in sync with the selected register's default.
  useEffect(() => {
    if (authState.cashRegisterSession) {
      setPrintEnabled(
        authState.cashRegisterSession.print_picking_receipt !== false
      );
    }
  }, [authState.cashRegisterSession]);

  // Prefill the basket when navigated here to fulfill a confirmed reservation.
  useEffect(() => {
    const res = location.state?.reservation;
    if (res && !prefillDone.current) {
      prefillDone.current = true;
      const items = (res.items || []).map((it) => ({
        id: `${it.article}-sale`,
        articleId: it.article,
        name: it.article_name,
        price: Number(it.price || 0),
        deposit_amount: Number(it.deposit_amount || 0),
        amount: it.quantity,
        isDepositReturn: false,
      }));
      setBasket(items);
      setReservationId(res.id);
      setReservationCustomerName(res.customer_name || '');
      setReservationCustomerEmail(res.customer_email || '');
      if (res.customer) setSelectedCustomer(res.customer);
      // Drop the router state so a refresh doesn't re-add the items.
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // --- Receipt printing queue ---------------------------------------------
  // Pop the next target off the queue, mount it, and trigger a print job. The
  // queue lets the picking receipt and the customer receipt print one after the
  // other when both are enabled.
  const runPrintQueue = useCallback(() => {
    const next = printQueueRef.current.shift();
    if (!next) return;
    setPrintTarget(next);
    setPrintSignal((n) => n + 1);
  }, []);

  const startPrint = useCallback(
    (targets) => {
      printQueueRef.current = [...targets];
      runPrintQueue();
    },
    [runPrintQueue]
  );

  // Fire the browser print dialog whenever a target is selected.
  useEffect(() => {
    if (printSignal > 0) {
      const t = setTimeout(() => window.print(), 80);
      return () => clearTimeout(t);
    }
  }, [printSignal]);

  // After each print job completes, advance to the next queued receipt.
  useEffect(() => {
    const onAfterPrint = () => setTimeout(runPrintQueue, 120);
    window.addEventListener('afterprint', onAfterPrint);
    return () => window.removeEventListener('afterprint', onAfterPrint);
  }, [runPrintQueue]);

  // Admin printer self-test: print a dummy picking receipt with 1-2 articles,
  // clearly marked as a test, so the thermal printer can be checked even when
  // there is no last sale to reprint.
  const printDummyReceipt = useCallback(() => {
    setDummySale({
      dummy: true,
      time: new Date().toISOString(),
      txId: 'TESTBELEG',
      customerName: 'Drucker-Test',
      items: [
        { amount: 2, name: 'Testartikel A' },
        { amount: 1, name: 'Testartikel B' },
      ],
    });
    startPrint(['dummy']);
  }, [startPrint]);

  // Fetch a transaction's customer receipt, then print it (used by auto-print
  // after a sale and by the manual reprint button).
  const printCustomerReceiptFor = useCallback(
    async (txId) => {
      if (!txId) return;
      try {
        const data = await fetchReceipt(txId).unwrap();
        setCustomerReceipt(data);
        startPrint(['customer']);
      } catch (error) {
        apiErrorToast(error, 'Kundenbeleg konnte nicht geladen werden.');
      }
    },
    [fetchReceipt, startPrint, apiErrorToast]
  );

  // Memoize articles that have stock
  const articlesWithStock = useMemo(() => {
    if (articlesLoading || stocksLoading || !articles || !stocks) {
      return [];
    }
    return articles.filter(
      (article) =>
        article &&
        article.id != null &&
        (typeof article.price === 'number' ||
          typeof article.price === 'string') &&
        stockMap.has(article.id)
    );
  }, [articles, articlesLoading, stocksLoading, stockMap, stocks]);

  // Memoize articles that are deposits
  const depositArticles = useMemo(() => {
    if (articlesLoading || !articles) {
      return [];
    }
    return articles.filter(
      (art) =>
        art &&
        art.id != null &&
        art.deposit === true &&
        (typeof art.deposit_amount === 'number' ||
          typeof art.deposit_amount === 'string')
    );
  }, [articles, articlesLoading]);

  // Calculate sums based on the current basket
  const { grossSum, depositSum, overallSum } = useMemo(() => {
    let currentGross = new Decimal(0);
    let currentDeposit = new Decimal(0);

    basket.forEach((item) => {
      const itemPrice = new Decimal(item.price || 0);
      const itemDeposit = new Decimal(item.deposit_amount || 0);

      if (item.isDepositReturn) {
        currentDeposit = currentDeposit.minus(itemDeposit.times(item.amount));
      } else {
        currentGross = currentGross.plus(itemPrice.times(item.amount));
        currentDeposit = currentDeposit.plus(itemDeposit.times(item.amount));
      }
    });

    const currentOverall = currentGross.plus(currentDeposit);

    return {
      grossSum: currentGross.toNumber(),
      depositSum: currentDeposit.toNumber(),
      overallSum: currentOverall.toNumber(),
    };
  }, [basket]);

  // --- Constants ---
  const amountLimitNoCustomer = 250;

  // Above €250 (incl. Pfand & VAT) a customer is mandatory (Kleinbetragsrechnung).
  // Derived from current state so it stays correct no matter how the basket was
  // populated — including reservation prefills that bypass the add handlers.
  const customerRequired = overallSum > amountLimitNoCustomer;
  const blockForCustomer = customerRequired && selectedCustomer === '';

  // --- Utility Functions ---
  const formatPriceLine = useCallback(
    (price, deposit, isDepositReturn = false) => {
      const formattedPrice = new Decimal(price || 0).toFixed(2);
      const formattedDeposit = new Decimal(deposit || 0).toFixed(2);

      if (isDepositReturn) {
        return `-${formattedDeposit} € Pfand`;
      }

      if (
        new Decimal(price || 0).greaterThan(0) &&
        new Decimal(deposit || 0).greaterThan(0)
      ) {
        return `${formattedPrice} € (+ ${formattedDeposit} € Pfand)`;
      }
      if (
        new Decimal(price || 0).greaterThan(0) &&
        new Decimal(deposit || 0).equals(0)
      ) {
        return `${formattedPrice} €`;
      }
      if (
        new Decimal(price || 0).equals(0) &&
        new Decimal(deposit || 0).greaterThan(0)
      ) {
        return `${formattedDeposit} € Pfand`;
      }
      return '0.00 €';
    },
    []
  );

  // --- Event Handlers ---

  const addArticleToReceipt = useCallback(
    (article, isDepositReturn = false) => {
      if (
        !article ||
        article.id == null ||
        (article.price == null && !isDepositReturn) ||
        (article.deposit_amount == null && article.deposit)
      ) {
        toast.error('Fehler: Artikeldaten unvollständig.');
        console.error('Attempted to add incomplete article:', article);
        return;
      }

      const isStockItem = !isDepositReturn;

      const existingItemIndex = basket.findIndex(
        (item) =>
          item.articleId === article.id &&
          item.isDepositReturn === isDepositReturn
      );

      const newPrice = Number(article.price || 0);
      const newDeposit = Number(article.deposit_amount || 0);

      const currentAmountInBasket =
        existingItemIndex !== -1 ? basket[existingItemIndex].amount : 0;
      const availableStock = stockMap.get(article.id) || 0;

      if (isStockItem) {
        if (currentAmountInBasket + 1 > availableStock) {
          toast.error(
            `Nicht genug Lagerbestand für ${article.name}. Verfügbar: ${availableStock}.`
          );
          return;
        }
      }

      const valueToAdd = new Decimal(newPrice).plus(newDeposit).toNumber();
      if (
        !isDepositReturn &&
        new Decimal(overallSum).plus(valueToAdd).toNumber() >
          amountLimitNoCustomer &&
        selectedCustomer === ''
      ) {
        toast.error(
          `Gesamtsumme ist > ${new Decimal(amountLimitNoCustomer).toFixed(2)}€, bitte zuerst Kunden auswählen.`
        );
        return;
      }

      if (existingItemIndex !== -1) {
        setBasket((prevBasket) => {
          const updatedBasket = [...prevBasket];
          updatedBasket[existingItemIndex] = {
            ...updatedBasket[existingItemIndex],
            amount: updatedBasket[existingItemIndex].amount + 1,
          };
          return updatedBasket;
        });
      } else {
        const newItem = {
          id: Date.now(),
          articleId: article.id,
          name: isDepositReturn
            ? `Pfandrückgabe ${article.name}`
            : article.name,
          price: newPrice,
          deposit_amount: newDeposit,
          amount: 1,
          isDepositReturn: isDepositReturn,
        };
        setBasket((prevBasket) => [...prevBasket, newItem]);
      }
    },
    [basket, overallSum, selectedCustomer, stockMap, amountLimitNoCustomer]
  );

  const handleAmountChange = useCallback(
    (articleId, newAmountStr, isDepositReturn = false) => {
      const newAmount = parseInt(newAmountStr, 10);

      const itemIndex = basket.findIndex(
        (item) =>
          item.articleId === articleId &&
          item.isDepositReturn === isDepositReturn
      );

      if (itemIndex === -1) {
        return;
      }

      const currentItem = basket[itemIndex];

      if (newAmountStr === '') {
        setBasket((prevBasket) =>
          prevBasket.map((item) =>
            item.articleId === articleId &&
            item.isDepositReturn === isDepositReturn
              ? { ...item, amount: currentItem.amount }
              : item
          )
        );
        return;
      }

      if (isNaN(newAmount) || newAmount < 0) {
        toast.error('Ungültige Menge eingegeben.');
        setBasket((prevBasket) =>
          prevBasket.filter(
            (item) =>
              !(
                item.articleId === articleId &&
                item.isDepositReturn === isDepositReturn
              )
          )
        );
        return;
      }

      if (!isDepositReturn) {
        const availableStock = stockMap.get(currentItem.articleId) || 0;
        if (newAmount > availableStock) {
          toast.error(
            `Nicht genug Lagerbestand für ${currentItem.name}. Verfügbar: ${availableStock}.`
          );
          return;
        }
      }

      // The €250-needs-a-customer gate is derived from state (blockForCustomer),
      // so updating the amount is all we do here.
      setBasket((prevBasket) =>
        prevBasket
          .map((item) =>
            item.articleId === articleId &&
            item.isDepositReturn === isDepositReturn
              ? { ...item, amount: newAmount }
              : item
          )
          .filter((item) => item.amount > 0)
      );
    },
    [stockMap, basket]
  );

  const handleCustomerChange = useCallback((e) => {
    setSelectedCustomer(e.target.value);
  }, []);

  // NEW: Function to open the PaymentModal
  const openPaymentModal = useCallback(() => {
    if (!authState.cashRegisterSession?.id) {
      toast.error('Bitte zuerst eine Kassensitzung auswählen.');
      return;
    }
    if (basket.length === 0) {
      toast.error('Der Warenkorb ist leer.');
      return;
    }
    if (blockForCustomer) {
      toast.error(
        `Ab ${new Decimal(amountLimitNoCustomer).toFixed(2)}€ muss ein Kunde ausgewählt werden.`
      );
      return;
    }
    setShowPaymentModal(true);
  }, [
    authState.cashRegisterSession,
    basket,
    blockForCustomer,
    amountLimitNoCustomer,
  ]);

  // handlePayment logic moved into this function, called by PaymentModal's submit
  const handleProcessPayment = useCallback(
    async (comment = '') => {
      const paymentData = {
        session: authState.cashRegisterSession.id,
        articles: basket.map((item) => ({
          article_name: item.name,
          article: item.articleId,
          amount: item.amount,
          is_deposit_return: item.isDepositReturn,
        })),
        customer: selectedCustomer || null,
        sum_gross: grossSum,
        sum_deposit: depositSum,
        sum_overall: overallSum,
        reservation: reservationId || undefined,
        comment: comment || '',
      };

      console.log('Payment data prepared for API submission:', paymentData);

      try {
        const response = await doProcessTx({
          sessionId: authState.cashRegisterSession.id,
          payload: paymentData,
        }).unwrap();

        if (response.tse_sync_required) {
          toast.warning(
            response.soft_fail_message ||
              'Verkauf gespeichert. TSE-Signatur wird nachgeholt.',
            { autoClose: 8000 }
          );
        } else if (response.ereceipt_pending) {
          toast.warning(
            response.soft_fail_message ||
              'Verkauf signiert. eReceipt wird nachgereicht.',
            { autoClose: 8000 }
          );
        } else {
          toast.success('Bezahlung erfolgreich verarbeitet!');
        }

        setLastTxId(response.id);

        // Build the warehouse picking receipt (goods only, no deposit returns).
        const pickItems = basket
          .filter((item) => !item.isDepositReturn)
          .map((item) => ({ name: item.name, amount: item.amount }));
        const customerName =
          reservationCustomerName ||
          marktCustomers?.find((c) => c.id === selectedCustomer)?.name ||
          '';
        if (pickItems.length > 0) {
          setLastSale({
            items: pickItems,
            customerName,
            txId: response.id,
            time: new Date().toISOString(),
          });
        }

        // Queue auto-prints: picking receipt (if enabled) and/or the customer
        // fiscal receipt (off by default). The customer receipt is fetched from the
        // backend (it carries the TSE data + QR codes).
        const wantPicking = printEnabled && pickItems.length > 0;
        const wantCustomer = printCustomerReceipt && !!response.id;
        if (wantCustomer) {
          try {
            const data = await fetchReceipt(response.id).unwrap();
            setCustomerReceipt(data);
            startPrint(wantPicking ? ['picking', 'customer'] : ['customer']);
          } catch (err) {
            apiErrorToast(err, 'Kundenbeleg konnte nicht geladen werden.');
            if (wantPicking) startPrint(['picking']);
          }
        } else if (wantPicking) {
          startPrint(['picking']);
        }

        // Clear basket and reset customer after successful transaction
        setBasket([]);
        setSelectedCustomer('');
        setReservationId(null);
        setReservationCustomerName('');
        setReservationCustomerEmail('');
        prefillDone.current = false;
        setShowPaymentModal(false);
      } catch (error) {
        console.error('Fehler beim Verarbeiten der Transaktion:', error);
        apiErrorToast(error, 'Unbekannter Fehler bei der Bezahlung.');
      }
    },
    [
      authState.cashRegisterSession,
      basket,
      selectedCustomer,
      grossSum,
      depositSum,
      overallSum,
      doProcessTx,
      reservationId,
      reservationCustomerName,
      marktCustomers,
      printEnabled,
      printCustomerReceipt,
      fetchReceipt,
      startPrint,
      apiErrorToast,
    ]
  );

  return (
    <div className='container-fluid'>
      <SelectCashRegisterModal
        runningSessions={runningSessions}
        show={!authState.cashRegisterSession}
      />
      {responseDebug && (
        <div className='alert alert-danger' role='alert'>
          {responseDebug}
        </div>
      )}

      <h3>Verkauf Getränkemarkt</h3>
      <h6 className='text-muted'>
        Kasse:{' '}
        {authState?.cashRegisterSession?.cash_register_name ?? 'nicht gewählt'}{' '}
        / CR-Session ID: {authState?.cashRegisterSession?.id ?? '-'}
      </h6>
      {reservationId && (
        <div className='alert alert-warning py-2' role='alert'>
          <i className='fas fa-box me-2' />
          Reservierung wird kassiert
          {reservationCustomerName ? ` – ${reservationCustomerName}` : ''}.
        </div>
      )}
      <hr />

      <div className='receipt row'>
        {/* Left Column - Articles */}
        <div className='col-sm-8' id='art_col'>
          {/* Customer Selection */}
          <div className='input-group mb-3 mt-3'>
            <div className='input-group-prepend'>
              <label className='input-group-text' htmlFor='customerSelect'>
                Kunden auswählen
              </label>
            </div>
            <select
              name='customer'
              className='form-select'
              id='customerSelect'
              value={selectedCustomer}
              onChange={handleCustomerChange}
            >
              <option value=''>nix ausgewählt</option>
              {customersLoading ? (
                <option disabled>Kunden werden geladen...</option>
              ) : (
                marktCustomers?.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}{' '}
                    {customer.contact ? `/ ${customer.contact}` : ''}
                  </option>
                ))
              )}
            </select>
            <CreateCustomerModal
              initialValues={
                reservationId && !selectedCustomer
                  ? {
                      name: reservationCustomerName,
                      contact_email: reservationCustomerEmail,
                    }
                  : undefined
              }
              onCreated={(c) => c?.id && setSelectedCustomer(c.id)}
            />
          </div>

          {/* Articles in Stock */}
          <div className='mb-3'>
            <h4>Artikel im Bestand</h4>
            <div className='d-flex flex-wrap gap-2'>
              {' '}
              {articlesLoading || stocksLoading ? (
                <p>Artikel werden geladen...</p>
              ) : articlesWithStock.length === 0 ? (
                <p className='text-muted'>
                  Keine Artikel mit Bestand gefunden.
                </p>
              ) : (
                articlesWithStock.map((art) => {
                  const availableQuantity = stockMap.get(art.id) || 0;

                  return (
                    <div
                      key={art.id}
                      className={`p-3 d-flex flex-column justify-content-between border rounded ${
                        availableQuantity === 0
                          ? 'bg-light text-muted'
                          : 'bg-primary text-white'
                      }`}
                      style={{
                        width: '200px',
                        height: '150px',
                        cursor:
                          availableQuantity === 0 ? 'not-allowed' : 'pointer',
                        opacity: availableQuantity === 0 ? 0.6 : 1,
                      }}
                      onClick={() =>
                        availableQuantity > 0 && addArticleToReceipt(art)
                      }
                    >
                      <div>
                        <h5 className='mb-1'>
                          <b>{art.name}</b>
                        </h5>
                        <small>Bestand: {availableQuantity}</small>
                      </div>
                      <div>
                        <strong className='fs-5'>
                          {new Decimal(art.price || 0).toFixed(2)} €
                        </strong>
                        <br />
                        {art.deposit && (
                          <small>
                            {' '}
                            (+ {new Decimal(art.deposit_amount || 0).toFixed(
                              2
                            )}{' '}
                            € Pfand)
                          </small>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Deposit Returns */}
          <div className='mt-4'>
            <h4>Pfandrückgaben</h4>
            <div className='d-flex flex-wrap gap-2'>
              {articlesLoading ? (
                <p>Artikel werden geladen...</p>
              ) : depositArticles.length === 0 ? (
                <p className='text-muted'>Keine Pfandartikel gefunden.</p>
              ) : (
                depositArticles.map((article) => (
                  <div
                    key={article.id}
                    className='p-3 d-flex flex-column justify-content-between border rounded bg-info text-white'
                    style={{
                      width: '200px',
                      height: '150px',
                      cursor: 'pointer',
                    }}
                    onClick={() => addArticleToReceipt(article, true)}
                  >
                    <div>
                      <h6 className='mb-1'>
                        <b>{article.name}</b>
                      </h6>
                    </div>
                    <div>
                      <strong className='fs-5'>
                        -{new Decimal(article.deposit_amount || 0).toFixed(2)} €
                      </strong>
                      <small> Pfand</small>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Receipt */}
        <div className='col-sm-4 card bg-light' id='receipt_col'>
          <div className='card-body'>
            <h5 className='card-title'>Kassenbon Getränkemarkt</h5>

            {/* Amount Error */}
            {blockForCustomer && (
              <div className='alert alert-danger' role='alert'>
                Gesamtsumme ist größer als{' '}
                <b>{new Decimal(amountLimitNoCustomer).toFixed(2)}€</b>, bitte
                Kunden auswählen. Vorher kann nicht weitermacht werden.
              </div>
            )}

            {/* Receipt List */}
            <ul className='list-group mb-3' id='receiptlist'>
              {basket.length === 0 ? (
                <li className='list-group-item text-muted text-center'>
                  Warenkorb ist leer
                </li>
              ) : (
                basket.map((item) => (
                  <li
                    key={`${item.articleId}-${item.isDepositReturn ? 'return' : 'sale'}`}
                    className='list-group-item d-flex justify-content-between align-items-center'
                  >
                    <span className='text-start'>
                      {item.name}
                      <br />
                      <small className='text-muted'>
                        {formatPriceLine(
                          item.price,
                          item.deposit_amount,
                          item.isDepositReturn
                        )}
                      </small>
                    </span>
                    <div className='d-flex align-items-center'>
                      <input
                        type='number'
                        min='0'
                        value={item.amount}
                        onChange={(e) =>
                          handleAmountChange(
                            item.articleId,
                            e.target.value,
                            item.isDepositReturn
                          )
                        }
                        onWheel={(e) => e.currentTarget.blur()}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                          }
                        }}
                        className='form-control form-control-sm text-center me-2'
                        style={{ width: '60px' }}
                      />
                      <button
                        type='button'
                        className='btn btn-light btn-sm'
                        onClick={() =>
                          handleAmountChange(
                            item.articleId,
                            0,
                            item.isDepositReturn
                          )
                        }
                      >
                        ❌
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>

            {/* Footer with Totals */}
            <div className='card-footer'>
              <p className='mb-1'>
                Summe brutto: <strong>{grossSum.toFixed(2)} €</strong>
              </p>
              <p className='mb-1'>
                Summe Pfand: <strong>{depositSum.toFixed(2)} €</strong>
              </p>
              <p className='mb-1'>
                Gesamtsumme: <strong>{overallSum.toFixed(2)} €</strong>
              </p>
            </div>

            {/* Replaced 'Bezahlvorgang' button with PaymentModal trigger */}
            <button
              className='btn btn-success w-100 mt-3'
              type='button'
              onClick={openPaymentModal} // Now opens the modal
              disabled={
                basket.length === 0 || blockForCustomer || processTxIsLoading
              }
            >
              Bezahlvorgang
            </button>

            {/* Picking receipt printing */}
            <div className='form-check mt-3 text-start'>
              <input
                className='form-check-input'
                type='checkbox'
                id='printToggle'
                checked={printEnabled}
                onChange={(e) => setPrintEnabled(e.target.checked)}
              />
              <label className='form-check-label' htmlFor='printToggle'>
                Abholbeleg drucken
              </label>
            </div>
            {/* Customer fiscal receipt — off by default (on demand). */}
            <div className='form-check text-start'>
              <input
                className='form-check-input'
                type='checkbox'
                id='printCustomerToggle'
                checked={printCustomerReceipt}
                onChange={(e) => setPrintCustomerReceipt(e.target.checked)}
              />
              <label className='form-check-label' htmlFor='printCustomerToggle'>
                Kundenbeleg drucken
              </label>
            </div>
            <button
              className='btn btn-outline-secondary w-100 mt-2'
              type='button'
              disabled={!lastSale}
              onClick={() => startPrint(['picking'])}
            >
              <i className='fas fa-print me-2' />
              Abholbeleg erneut drucken
            </button>
            <button
              className='btn btn-outline-secondary w-100 mt-2'
              type='button'
              disabled={!lastTxId}
              onClick={() => printCustomerReceiptFor(lastTxId)}
            >
              <i className='fas fa-receipt me-2' />
              Kundenbeleg drucken
            </button>
            {authState.isSuperUser && (
              <button
                className='btn btn-outline-warning w-100 mt-2'
                type='button'
                onClick={printDummyReceipt}
              >
                <i className='fas fa-vial me-2' />
                Test-Beleg drucken (Debug)
              </button>
            )}
          </div>
        </div>
      </div>

      {/* NEW: PaymentModal component */}
      <PaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        overallSum={overallSum}
        onProcessPayment={handleProcessPayment}
        isLoading={processTxIsLoading}
      />

      {/* Hidden receipts (printed via window.print()). Only the active target is
          mounted so a print job outputs exactly one receipt. */}
      {printTarget === 'customer' ? (
        <FiscalReceipt receipt={customerReceipt} />
      ) : printTarget === 'dummy' ? (
        <PickingReceipt sale={dummySale} />
      ) : (
        <PickingReceipt sale={lastSale} />
      )}
    </div>
  );
};

export default MarktPOSInterface;
